import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DatePicker } from 'primeng/datepicker';
import { SelectModule } from 'primeng/select';
import { BlockUI } from 'primeng/blockui';
import { ProgressSpinner } from 'primeng/progressspinner';
import { Button } from 'primeng/button';
import { ResultsApi } from '@features/reports/services/reports-results-api';
import { BusinessStateService } from '@core/services/business-state.service';
import { ResultsList } from '@features/reports/components/results-list/results-list';
import { ReportResultResponse } from '@features/reports/models/results.model';
import { Subject, takeUntil, skip } from 'rxjs';

interface DropdownOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-results',
  imports: [FormsModule, DatePicker, SelectModule, ResultsList, BlockUI, ProgressSpinner, Button],
  templateUrl: './results.html',
  styleUrl: './results.scss',
})
export class Results implements OnInit, OnDestroy {
  private readonly resultsApi = inject(ResultsApi);
  private readonly businessStateService = inject(BusinessStateService);
  private readonly destroy$ = new Subject<void>();

  reportResults: ReportResultResponse[] = [];
  isLoading = false;

  pcgOptions: DropdownOption[] = [
    { label: 'AR4', value: 'AR4' },
    { label: 'AR5', value: 'AR5' },
    { label: 'AR6', value: 'AR6' },
  ];

  selectedYear: Date = new Date();
  selectedPCG: string = 'AR6';

  ngOnInit(): void {
    this.fetchReportResults();

    // Suscribirse a cambios en la selección del treeselect del header
    // skip(1) para evitar ejecutar dos veces inicialmente
    this.businessStateService.selectedBusiness$
      .pipe(
        skip(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        // Actualizar resultados cuando cambie la selección
        this.fetchReportResults();
      });
  }

  fetchReportResults() {
    if (this.selectedYear && this.selectedPCG) {
      const year = this.selectedYear.getFullYear();
      const pcg = this.selectedPCG;
      const selectedBusiness = this.businessStateService.getSelectedBusiness();
      const selectedId = selectedBusiness?.data?.id;

      if (!selectedId) {
        console.error('No hay negocio o sede seleccionada');
        return;
      }

      const isAll = selectedId === 'all';
      const isOffice = this.businessStateService.isOfficeSelected();

      const requestParams = isAll
        ? { year, pcg }
        : isOffice
          ? { officeId: selectedId, year, pcg }
          : { businessId: selectedId, year, pcg };

      this.isLoading = true;
      this.reportResults = []; // Limpiar resultados anteriores
      this.resultsApi.getReportResults(requestParams).subscribe({
        next: (response) => {
          console.log('Report Results:', response);
          this.reportResults = response.data || [];
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error fetching report results:', error);
          this.reportResults = [];
          this.isLoading = false;
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
